import { RefreshCw, CheckCircle2, Clock, ExternalLink, FileText } from "lucide-react";

import { Button } from "../../components/ui/button";
import { prettySize, formatTime } from "../../lib/format";

function CacheBadge({ cached }) {
  if (cached) {
    return (
      <span className="inline-flex items-center gap-1.5 text-xs text-[#22C55E]">
        <CheckCircle2 className="h-4 w-4" strokeWidth={1.5} />
        Cached
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1.5 text-xs text-[#5B695E]">
      <Clock className="h-4 w-4" strokeWidth={1.5} />
      Not cached
    </span>
  );
}

function RangeCardMetrics({ r }) {
  return (
    <div className="mt-6 space-y-3 text-sm">
      <div className="flex gap-2 items-start">
        <FileText className="h-4 w-4 mt-0.5 text-[#8A9A86] shrink-0" strokeWidth={1.5} />
        <div className="min-w-0">
          <div className="text-[11px] uppercase tracking-[0.2em] text-[#5B695E]">PDF</div>
          <div className="font-mono text-xs break-all text-[#111812]">
            {r.pdf_filename || "—"}
          </div>
        </div>
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div>
          <div className="text-[11px] uppercase tracking-[0.2em] text-[#5B695E]">Pages</div>
          <div>{r.page_count ?? "—"}</div>
        </div>
        <div>
          <div className="text-[11px] uppercase tracking-[0.2em] text-[#5B695E]">Size</div>
          <div>{prettySize(r.size_bytes)}</div>
        </div>
      </div>
      <div>
        <div className="text-[11px] uppercase tracking-[0.2em] text-[#5B695E]">DEP last modified</div>
        <div className="text-[#5B695E]">{r.last_modified || "—"}</div>
      </div>
      <div>
        <div className="text-[11px] uppercase tracking-[0.2em] text-[#5B695E]">Cached at</div>
        <div className="text-[#5B695E]">{formatTime(r.updated_at)}</div>
      </div>
    </div>
  );
}

export default function RangeCard({ r, refreshing, onRefresh }) {
  return (
    <div
      data-testid={`range-card-${r.slug}`}
      className="bg-white border border-border/60 rounded-2xl shadow-sm p-6 flex flex-col"
    >
      <div className="flex items-start justify-between gap-3">
        <div>
          <div className="text-[11px] uppercase tracking-[0.2em] text-[#5B695E]">Range</div>
          <div className="font-heading text-2xl tracking-tight mt-1">{r.slug}</div>
        </div>
        <CacheBadge cached={r.cached} />
      </div>

      <RangeCardMetrics r={r} />

      <div className="mt-6 flex items-center gap-3">
        <Button
          onClick={() => onRefresh(r.slug)}
          disabled={!!refreshing}
          data-testid={`range-refresh-${r.slug}`}
          size="sm"
          className="bg-[#1A3626] hover:bg-[#2D4A3A] text-white"
        >
          <RefreshCw
            className={`h-4 w-4 mr-2 ${refreshing ? "animate-spin" : ""}`}
            strokeWidth={1.5}
          />
          {refreshing ? "Refreshing…" : "Check for new version"}
        </Button>
        <a
          href={r.pdf_url || r.page_url}
          target="_blank"
          rel="noopener noreferrer"
          data-testid={`range-link-${r.slug}`}
          className="text-xs text-[#1A3626] hover:text-[#2D4A3A] inline-flex items-center gap-1"
        >
          <ExternalLink className="h-3.5 w-3.5" strokeWidth={1.5} />
          DEP page
        </a>
      </div>
    </div>
  );
}
